import { useCallback } from 'react';
import { useSiloConfiguration } from '@silo/config-legacy';
import { ChecksumAddress } from '@silo/types';
import { useSiloWeb3 } from '@silo/web3';

/**
 * Returns whether an asset is frozen at a given silo level, or globally at the protocol level.
 */
const useIsSiloAssetFrozen = () => {
  const { protocolInstance } = useSiloWeb3();
  const { getConfigurationForSilo } = useSiloConfiguration(protocolInstance);

  return useCallback(
    (assetAddress: ChecksumAddress, siloAddress: ChecksumAddress) => {
      const { frozen: siloFrozen, getConfigurationForAsset } = getConfigurationForSilo(siloAddress);
      const { frozen: assetFrozen } = getConfigurationForAsset(assetAddress);

      return siloFrozen || assetFrozen;
    },
    [getConfigurationForSilo]
  );
};

/**
 * Returns whether a given asset is frozen at a given silo level, or globally at the protocol level.
 */
export const useIsAssetActionFrozen = (action: 'deposit' | 'borrow' | 'withdraw' | 'repay') => {
  const isAssetFrozen = useIsSiloAssetFrozen();
  const { protocolInstance } = useSiloWeb3();
  const { getConfigurationForSilo } = useSiloConfiguration(protocolInstance);

  return useCallback(
    (assetAddress: ChecksumAddress, siloAddress: ChecksumAddress) => {
      const { getConfigurationForAsset } = getConfigurationForSilo(siloAddress);
      const { actions } = getConfigurationForAsset(assetAddress);
      const actionIsFrozen = actions[action].frozen;

      return isAssetFrozen(assetAddress, siloAddress) || actionIsFrozen;
    },
    [getConfigurationForSilo, action, isAssetFrozen]
  );
};
